import TemplateMixin from './TemplateMixin';
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { PaneDropdown } from 'streamlabs-beaker';
import { IWebcamComponent, IMediaInputDevices, IComponent } from '@/helpers/editor';
import NotificationHelper from '@/helpers/notification';
import { Throttle } from 'lodash-decorators';
import { ESocketEvent } from '@/helpers/socket';

const DEFAULT_USER_CONSTRAINTS = {
  video: {
    width: 1280,
    height: 720,
    aspectRatio: 16 / 9,
    frameRate: 30,
  },
  audio: {
    echoCancellation: true,
  },
};


enum EFacingMode {
  user = 'user',
  environment = 'environment',
}

@Component({
  components: {
    SPaneDropdown: PaneDropdown,
  }
})
export default class WebcamTemplate extends Mixins(TemplateMixin) {
  $refs!: {
    webcamVideoElement: HTMLVideoElement;
  };

  hasTrack: boolean = true;
  videoIsPlaying: boolean = false;
  playPromise: Promise<any> = null;
  warningMessage: string = null;

  async mounted() {
    if (navigator.userAgent.toLowerCase().includes('ipad') && navigator.userAgent.toLowerCase().includes('crios')) {
      this.warningMessage = 'Webcam may not work as intended on iOS Chrome.  Please use Safari.';
      return;
    }

    await this.init();
  }

  get hasDeviceId(): boolean {
    if (!this.videoInputDevices) return false;
    return this.videoInputDevices.every(device => device.deviceId);
  }

  get hasAudioDeviceId(): boolean {
    if (!this.audioInputDevices) return false;
    return this.audioInputDevices.every(device => device.deviceId);
  }

  get volume(): number {
    return (this.component as IWebcamComponent)?.volume || (this.component as IWebcamComponent).volume === 0 ?
      (this.component as IWebcamComponent).volume :
      100;
  }

  async init() {
    if (this.isEditor) {
      await this.getDevices();
      this.bindExistingDevices();
      if (this.isMobile || !this.hasDeviceId) {
        document.addEventListener('visibilitychange', this.handleVisibilityChange, false);
        await this.selectedFacingModeOnChange(false);
      } else {
        await this.selectedInputDeviceOnChange(false);
      }

      if (!this.webcamStream && !this.isPreview)
        this.sendSocketEvent(ESocketEvent.webcamBroadcaster);
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.sendSocketEvent(ESocketEvent.webcamBroadcasterDisconnect);
    } else {
      this.sendSocketEvent(ESocketEvent.webcamBroadcaster);
    }
  }

  get facingModeLabel(): string {
    return this.selectedFacingMode === EFacingMode.user ? 'Front Camera' : 'Back Camera';
  }

  getDeviceLabel(device) {
    return device.label;
  }

  async getDevices() {
    const inputDevices = await navigator.mediaDevices.enumerateDevices();

    this.videoInputDevices = inputDevices.filter(device => device.kind === 'videoinput');
    this.audioInputDevices = inputDevices.filter(device => device.kind === 'audioinput');
  }

  bindExistingDevices() {
    if (this.videoInputDevices.length && this.hasDeviceId) {
      let selectedVideoDevice = null;
      if ((this.component as IWebcamComponent).videoDeviceId || (this.component as IWebcamComponent).deviceId) {
        selectedVideoDevice = this.videoInputDevices.find((device) =>
          device.deviceId === (this.component as IWebcamComponent).videoDeviceId
        );
      }

      this.selectedVideoInputDevice = selectedVideoDevice || this.videoInputDevices[0];
    }

    if (this.audioInputDevices.length && this.hasAudioDeviceId) {
      let selectedAudioDevice = null;
      if ((this.component as IWebcamComponent).audioDeviceId) {
        selectedAudioDevice = this.audioInputDevices.find((device) =>
          device.deviceId === (this.component as IWebcamComponent).audioDeviceId
        );
      }

      this.selectedAudioInputDevice = selectedAudioDevice || this.audioInputDevices[0];
    }

    this.selectedFacingMode = (this.component as IWebcamComponent).facingMode;
  }

  @Watch('selectedVideoInputDevice')
  @Watch('selectedAudioInputDevice')
  async selectedInputDeviceOnChangeWatcher(newVal, oldVal) {
    if (this.isMobile || !this.hasDeviceId) return;

    const shouldSave = oldVal ? (newVal.videoDeviceId !== oldVal.videoDeviceId) : false;
    await this.selectedInputDeviceOnChange(shouldSave);
  }

  async selectedInputDeviceOnChange(shouldSave: boolean = true) {
    if (!this.isEditor) return;

    if (
      !(this.component as IWebcamComponent).videoDeviceId &&
      this.getHasFrontOrBackCamera(this.selectedVideoInputDevice.label)
    ) {
      this.selectedFacingModeOnChange(false);
      return;
    }

    const constraints = DEFAULT_USER_CONSTRAINTS;
    constraints.video['deviceId'] = { exact: this.selectedVideoInputDevice?.deviceId };
    constraints.audio['deviceId'] = { exact: this.selectedAudioInputDevice?.deviceId };

    if (shouldSave) this.onComponentUpdate({
      ...this.component,
      ...{
        videoDeviceId: this.selectedVideoInputDevice.deviceId,
        audioDeviceId: this.selectedAudioInputDevice.deviceId,
      }
    });

    await this.getWebcamFeed(constraints);
  }

  @Watch('selectedFacingMode')
  async selectedFacingModeOnChangeWatcher(newVal, oldVal) {
    if (!this.isMobile) return;

    const shouldSave = oldVal ? (newVal.videoDeviceId !== oldVal.videoDeviceId) : false;
    await this.selectedFacingModeOnChange(shouldSave);
  }

  async selectedFacingModeOnChange(shouldSave: boolean = true) {
    if (!this.isEditor) return;

    const constraints = DEFAULT_USER_CONSTRAINTS;
    delete constraints.video['deviceId'];

    constraints.video['facingMode'] = this.selectedFacingMode;

    if (shouldSave) await this.onComponentUpdate({ ...this.component, ...{ facingMode: this.selectedFacingMode } });

    await this.getWebcamFeed(constraints);
  }

  @Throttle(500, { leading: false, trailing: true })
  async getWebcamFeed(userMediaConstraints = DEFAULT_USER_CONSTRAINTS) {
    this.webcamStream = null;
    navigator.mediaDevices.getUserMedia(userMediaConstraints).then(async (stream: MediaStream) => {
      await this.getDevices();
      this.webcamStream = stream;
      this.hasTrack = true;

      this.$nextTick(() => {
        if (this.webcamStream && this.$refs.webcamVideoElement) {
          this.$refs.webcamVideoElement.srcObject = this.webcamStream;

          this.$refs.webcamVideoElement.addEventListener('loadedmetadata', (e) => {
            if (this.$refs.webcamVideoElement) {
              const data: Partial<IComponent> = {
                id: this.component.id,
                aspectRatio: this.$refs.webcamVideoElement.videoWidth / this.$refs.webcamVideoElement.videoHeight,
              };

              this.onComponentUpdate(data, false);
            }
          });
        }
      });
    }).catch((err) => {
      if (this.isEditor) {
        let errorMessage = '';
        switch (err.name) {
          case 'NotReadableError':
            errorMessage = 'Camera access denied.  Please check if camera is occupied by other application.';
          default:
            errorMessage = err.message;
        }

        NotificationHelper.error(errorMessage);
      }
    });
  }

  get previewCssObject() {
    return {
      position: 'relative',
    };
  }

  get selectedDeviceLabel(): string {
    if (this.selectedVideoInputDevice && !this.getHasFrontOrBackCamera(this.selectedVideoInputDevice.label)) {
      return this.selectedVideoInputDevice.label;
    } else {
      return this.selectedFacingMode === EFacingMode.user ? 'Front Camera' : 'Back Camera';
    }
  }

  getHasFrontOrBackCamera(label): boolean {
    return ['Front Camera', 'Back Camera'].includes(label);
  }

  setDevice(device: IMediaInputDevices) {
    switch (device.label) {
      case 'Back Camera':
        this.setMobileCameraFacing(EFacingMode.environment);
        return;
      case 'Front Camera':
        this.setMobileCameraFacing(EFacingMode.user);
        return;
      default:
        this.selectedVideoInputDevice = device;
        return;
    }
  }

  setMobileCameraFacing(facingMode) {
    if (this.selectedFacingMode === facingMode) return;
    this.selectedFacingMode = facingMode;
  }

  addToScreen() {
    const videoDeviceId = this.selectedVideoInputDevice && !this.getHasFrontOrBackCamera(this.selectedVideoInputDevice.label) ? this.selectedVideoInputDevice.deviceId : null;
    const data = {
      preview: false,
      facingMode: this.selectedFacingMode,
      videoDeviceId,
    };

    this.onComponentUpdate({ ...this.component, ...data });
    this.sendSocketEvent(ESocketEvent.webcamBroadcaster);
  }

  @Watch('volume')
  async volumeHandler() {
    this.$refs.webcamVideoElement.volume = this.volume / 100;
  }

  playStream(stream: MediaStream) {
    this.hasTrack = true;
    if (this.$refs.webcamVideoElement) {
      this.$refs.webcamVideoElement.volume = this.volume / 100;
      this.$refs.webcamVideoElement.srcObject = stream;
      this.playPromise = this.$refs.webcamVideoElement.play();
      this.playPromise.then(_ => { }).catch(error => NotificationHelper.error(error));
      this.videoIsPlaying = true;
    }
  }

  stopStream() {
    if (this.webcamStream) {
      this.webcamStream.getTracks().forEach(function (track) {
        track.stop();
      });
    }

    this.hasTrack = false;
    this.videoIsPlaying = false;
  }
}
